import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import { fetchMemberById } from "../script/rpc";

const PALETTE = [
  { border: "#7c3aed", bg: "#f5f3ff", text: "#5b21b6" },
  { border: "#0ea5e9", bg: "#f0f9ff", text: "#0369a1" },
  { border: "#059669", bg: "#f0fdf4", text: "#047857" },
  { border: "#d97706", bg: "#fffbeb", text: "#92400e" },
  { border: "#db2777", bg: "#fdf2f8", text: "#9d174d" },
  { border: "#0891b2", bg: "#ecfeff", text: "#0e7490" },
  { border: "#65a30d", bg: "#f7fee7", text: "#3f6212" },
];

function pal(d) { return PALETTE[(d || 0) % PALETTE.length]; }

function fmtCur(val) {
  if (!val && val !== 0) return "—";
  if (val === 0) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
}

function initials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "?";
}

export default function InfoDrawer({ member, onClose }) {
  const { auth } = useAuth();
  const { uid, password } = auth || {};
  const [sponsorCode, setSponsorCode] = useState("");

  useEffect(() => {
    if (!member || !member.parent_id || !member.parent_id[0]) {
      setSponsorCode("");
      return;
    }

    const pid = member.parent_id[0];
    (async () => {
      try {
        const parent = await fetchMemberById(uid, password, pid);
        if (parent) setSponsorCode(parent.mlm_sequence || "—");
      } catch (e) {
        console.error("Failed to fetch sponsor code:", e);
      }
    })();
  }, [member, uid, password]);

  if (!member) return null;
  const p = pal(member.depth);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 99,
          background: "rgba(15,23,42,0.25)",
          backdropFilter: "blur(3px)",
          animation: "fadeIn 0.2s ease",
        }}
      />

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 360, maxWidth: "95vw",
        background: "#fff",
        borderLeft: `3px solid ${p.border}`,
        boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        display: "flex", flexDirection: "column",
        zIndex: 100,
        animation: "slideRight 0.28s cubic-bezier(.4,0,.2,1)",
        overflowY: "auto",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${p.bg}, #fff)`,
          borderBottom: "1px solid #e2e8f0",
          padding: "18px 20px 14px",
          display: "flex", alignItems: "flex-start", gap: 14,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: `linear-gradient(135deg, ${p.border}, ${p.text})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800, fontSize: 18,
          }}>
            {initials(member.name)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", wordBreak: "break-word" }}>
              {member.name}
            </div>
            {member.post_id && (
              <div style={{
                display: "inline-block", marginTop: 4,
                background: p.border, color: "#fff",
                fontSize: 10, fontWeight: 700, padding: "2px 10px",
                borderRadius: 20,
              }}>
                {member.post_id[1] }
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 8, border: "1px solid #e2e8f0",
              background: "#f8fafc", cursor: "pointer", fontSize: 16, color: "#64748b",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>

          {/* Network stats */}
          <Section title="📊 Financial Overview">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <BigStat label="Team Size" value={member.total_direct_child ?? 0} color={p.border} bg={p.bg} />
              <BigStat label="Own Amount" value={fmtCur(member.total_amount)} color={p.border} bg={p.bg} />
              <BigStat label="Team Amount" value={fmtCur(member.child_total_amount)} color={p.border} bg={p.bg} />
            </div>
          </Section>
          
          {/* Identification */}
          <Section title="🪪 Identification">
            <InfoRow label="Email"     value={member.email || "No Email"} />
            <InfoRow label="Node #"   value={member.mlm_sequence || "—"} mono />
            <InfoRow label="Rank"     value={member.post_id ? member.post_id[1] : "No Rank"} />
            <InfoRow label="Sponsor Referral" value={sponsorCode || "—"} mono />
          </Section>

          {/* Status */}
          <Section title="✅ Status">
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 16px", borderRadius: 20,
              background: member.status === "active" ? "#f0fdf4" : member.status === "deactive" ? "#fef2f2" : "#f8fafc",
              border: `1px solid ${member.status === "active" ? "#bbf7d0" : member.status === "deactive" ? "#fecaca" : "#e2e8f0"}`,
              color: member.status === "active" ? "#15803d" : member.status === "deactive" ? "#dc2626" : "#64748b",
              fontWeight: 700, fontSize: 13,
            }}>
              <div style={{ 
                width: 8, height: 8, borderRadius: "50%", 
                background: member.status === "active" ? "#22c55e" : member.status === "deactive" ? "#ef4444" : "#94a3b8" 
              }} />
              {member.status === "active" ? "Active" : member.status === "deactive" ? "Deactive" : "Not Member"}
            </div>
          </Section>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn    { from { opacity:0; } to { opacity:1; } }
        @keyframes slideRight { from { transform:translateX(100%); } to { transform:translateX(0); } }
        @keyframes spin       { to   { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, small }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "8px 12px", background: "#f8fafc", borderRadius: 8,
      border: "1px solid #e2e8f0", marginBottom: 6, gap: 12,
    }}>
      <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: small ? 11 : 12, fontWeight: 600, color: "#0f172a",
        fontFamily: mono ? "monospace" : "inherit",
        wordBreak: "break-all", textAlign: "right",
      }}>{value}</span>
    </div>
  );
}

function BigStat({ label, value, color, bg }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "10px 6px", background: bg, borderRadius: 10,
      border: `1.5px solid ${color}25`,
    }}>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
      <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500, marginTop: 2 }}>{label}</span>
    </div>
  );
}
